import {gsap} from "gsap";
import {Config} from "../config/Config";
import {Symbol} from "../reels/Symbol";
import {Game} from "../Game";
import {SymbolContainer} from "../reels/SymbolContainer";
import {SimpleReelModelSymbolData} from "../../core/reel/simplereel/SimpleReelModel";
import {GameFeatureMode, Mental2SymbolStates} from "../enums/Enum";
import {TimelineSprite} from "../../core/animation/TimelineSprite";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";

export class GameUtils {
    /**
     * @param spine PIXI.spineSpine object
     * @param trackIndex
     * @param animationName
     * @param loop
     * @param animationAlpha
     * @param mixDuration
     * @param resetOnComplete
     * @private
     */
    public static createTimelineForAnimation(spine: PIXI.spine.Spine, trackIndex: number, animationName: string, loop: boolean, animationAlpha: number = 1, mixDuration: number = 0, resetOnComplete: boolean = true): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline({
            repeat: loop ? -1 : 0
        });

        if (!spine.spineData || !spine.state) {
            return tl;
        }

        const animation: PIXI.spine.core.Animation = spine.spineData.findAnimation(animationName);
        if (!animation) {
            console.error(`No animation named ${animationName}`);

            return tl;
        }

        spine.autoUpdate = false;

        const setAnimation: () => void = () => {
            if (spine.state) {
                const trackEntry: PIXI.spine.core.TrackEntry = spine.state.setAnimationWith(trackIndex, animation, false);
                trackEntry.alpha = animationAlpha;
                trackEntry.mixDuration = mixDuration;
            }
        };

        const target = {
            playHead: 0,
            spine: spine,
            prevTime: 0
        };

        const updateFunc: () => void = () => {
            if (spine && target && spine.state) {
                const now = target.playHead;
                spine.update(now - target.prevTime);
                target.prevTime = now;
            }
        };

        tl.add(() => {
            target.prevTime = 0;
            setAnimation();
        });
        tl.fromTo(target, {playHead: 0}, {
            duration: animation.duration,
            playHead: animation.duration,
            onUpdate: updateFunc,
            ease: "none"
        });
        if (resetOnComplete) {
            tl.add(() => {
                target.prevTime = 0;
                setAnimation();
            });
        }
        tl.to(target, {duration: 0.0001}); //Needed so gsap does not skip "setAnimation" during reverse play.
        tl.add(() => {
            target.playHead = animation.duration;
            updateFunc();
        });

        return tl;
    }

    public static getSymbolPos(reelId: number, rowId: number): PIXI.Point {
        const x: number = Config.SYMBOL_WIDTH * reelId + (Config.SYMBOL_WIDTH * 0.5);
        let y: number = (Config.SYMBOL_HEIGHT * rowId) + (Config.SYMBOL_HEIGHT_HALF) + 117; //216-99
        if (reelId === 2) {
            y -= 99;
            if (rowId === 0) {
                y += 49;
            }
        } else if (reelId === 1 || reelId === 3) {
            y += Config.SYMBOL_HEIGHT_HALF
        }

        return new PIXI.Point(x, y);
    }

    public static animStringByState(state: Mental2SymbolStates) {
        let animString: string = "";
        switch (state) {
            case Mental2SymbolStates.SPIN:
                animString = "blur";
                break;
            case Mental2SymbolStates.ENHANCER_REVEAL:
                animString = "99";
                break;
            case Mental2SymbolStates.WIN:
                animString = "198_to_198";
                break;
            case Mental2SymbolStates.ONE_FORTY_EIGHT:
                animString = "198_to_148";
                break;
            case Mental2SymbolStates.ONE_THIRTY_TWO:
                animString = "148_to_132";
                break;
            case Mental2SymbolStates.ONE_HUNDRED_EIGHTEEN:
                animString = "132_to_118";
                break;
            case Mental2SymbolStates.NINETY_NINE:
                animString = "118_to_99";
                break;
            case Mental2SymbolStates.EIGHTY_FOUR:
                animString = "99_to_84";
                break;
            case Mental2SymbolStates.SEVENTY_FOUR:
                animString = "84_to_74";
                break;
            case Mental2SymbolStates.SIXTY_SIX:
                animString = "74_to_66";
                break;
            case Mental2SymbolStates.SIXTY:
                animString = "66_to_60";
                break;
            case Mental2SymbolStates.FIFTY_SIX:
                animString = "60_to_56";
                break;
            case Mental2SymbolStates.FIFTY:
                animString = "56_to_50";
                break;
            case Mental2SymbolStates.FORTY_FIVE:
                animString = "50_to_45";
                break;
            case Mental2SymbolStates.FORTY:
                animString = "45_to_40";
                break;
            case Mental2SymbolStates.THIRTY_TWO:
                animString = "40_to_32";
                break;
            case Mental2SymbolStates.TWENTY_SIX:
                animString = "32_to_26";
                break;
            default:
                animString = "198_to_198";
        }
        return animString;
    }

    public static getReSizeState(actualHeight: number): Mental2SymbolStates {
        let state: Mental2SymbolStates = Mental2SymbolStates.NO_WIN;
        switch (actualHeight) {
            case 198:
                state = Mental2SymbolStates.ONE_NINETY_EIGHT;
                break;
            case 148:
                state = Mental2SymbolStates.ONE_FORTY_EIGHT;
                break;
            case 132:
                state = Mental2SymbolStates.ONE_THIRTY_TWO;
                break;
            case 118:
                state = Mental2SymbolStates.ONE_HUNDRED_EIGHTEEN;
                break;
            case 99:
                state = Mental2SymbolStates.NINETY_NINE;
                break;
            case 84:
                state = Mental2SymbolStates.EIGHTY_FOUR;
                break;
            case 74:
                state = Mental2SymbolStates.SEVENTY_FOUR;
                break;
            case 66:
                state = Mental2SymbolStates.SIXTY_SIX;
                break;
            case 60:
                state = Mental2SymbolStates.SIXTY;
                break;
            case 56:
                state = Mental2SymbolStates.FIFTY_SIX;
                break;
            case 50:
                state = Mental2SymbolStates.FIFTY;
                break;
            case 45:
                state = Mental2SymbolStates.FORTY_FIVE;
                break;
            case 40:
                state = Mental2SymbolStates.FORTY;
                break;
            case 32:
                state = Mental2SymbolStates.THIRTY_TWO;
                break;
            case 26:
                state = Mental2SymbolStates.TWENTY_SIX;
                break;
        }
        return state;
    }

    public static getAngleInDegree(pos1: number[], pos2: number[]): number {
        let angle: number = Math.atan2(pos1[0] - pos2[0], pos1[1] - pos2[1]);
        angle = angle < 0 ? (2 * Math.PI + angle) : angle;
        const finalAngle: number = angle * (180 / Math.PI);
        return 360 - 90 - finalAngle; //pixi object use clockwise angle.
    }

    public static getDistance(p1: PIXI.Point, p2: PIXI.Point): number {
        const xDist: number = p2.x - p1.x;
        const yDist: number = p2.y - p1.y;
        return Math.sqrt(Math.pow(xDist, 2) + Math.pow(yDist, 2));
    }

    public static createSprite(assetStr: string, pos?: PIXI.Point | undefined, isCenter?: boolean): PIXI.Sprite {
        const sprite: PIXI.Sprite = new PIXI.Sprite(Game.assetLoader.getTextures(assetStr)[0]);
        if (pos) {
            sprite.position.set(pos.x, pos.y);
        }
        if (isCenter) {
            sprite.anchor.set(0.5);
        }
        return sprite;
    }

    public static createTimelineSprite(assetStr: string, sourceFPS: number = 30, pos?: PIXI.Point | undefined, isCenter?: boolean): TimelineSprite {
        const tls: TimelineSprite = new TimelineSprite(Game.assetLoader.getTextures(assetStr), sourceFPS);
        if (pos) {
            tls.position.set(pos.x, pos.y);
        }
        if (isCenter) {
            tls.anchor.set(0.5);
        }
        return tls;
    }

    public static getSymbol(reelId: number, rowId: number): SymbolContainer {
        return <SymbolContainer>Game.reelView.reelModels[reelId].getStopSymbolAt(rowId).positionable;
    }

    public static getReelSizeWithEnhancer(is1stEnhancerOpen: boolean, is2ndEnhancerOpen: boolean, is3rdEnhancerOpen: boolean): number[] {
        const symInReels: number[] = Config.SYMBOLS_IN_REEL.concat([]);
        if (is1stEnhancerOpen) {
            symInReels[1]++;
        }
        if (is2ndEnhancerOpen) {
            symInReels[2]++;
        }
        if (is3rdEnhancerOpen) {
            symInReels[3]++;
        }
        return symInReels;
    }

    public static resetSpineItem(item: PIXI.spine.Spine): void {
        item.skeleton.setSlotsToSetupPose();
        item.skeleton.setToSetupPose();
        item.state.clearTracks();
        item.state.setEmptyAnimation(0, 0);

        if (!item.visible) {
            item.visible = true;
            item.updateTransform();
        }
    }

    public static bigWinAnticipationDuration(): number {
        const level: number = Game.model.getBigWinLevels().length;
        let duration: number = 0;
        switch (level) {
            case 2:
                duration = 0.2333;
                break;
            case 3:
                duration = 0.2333;
                break;
            case 4:
                duration = 0.4333;
                break;
            case 5:
                duration = 0.6667;
                break;
            case 6:
                duration = 0.9667;
                break;
        }
        return duration;
    }

    public static isBonusTriggeredThisSpin(mode: string, nextMode: string): boolean {
        return mode == GameFeatureMode.NORMAL && (nextMode == GameFeatureMode.FREESPIN || nextMode == GameFeatureMode.FREESPIN_SUPER || nextMode == GameFeatureMode.FREESPIN_COOL);
    }

    public static shake(x: number, y: number, hertz: number, intensity: number, duration: number): gsap.core.Timeline {
        const gameStage = NolimitApplication.pixiApp.stage.children[0];
        let startPosY: number,
            startPosX: number,
            t: boolean = true;
        const tl: gsap.core.Timeline = gsap.timeline({
            onStart: () => {
                startPosY = 0;
                startPosX = 0;
            },
            onComplete: () => {
                gameStage.x = 0;
                gameStage.y = 0;
            },
            onUpdate: () => {
                const lt: number = Math.random() * 2,
                    et: number = Math.random();
                if (t) {
                    t = false;
                    return;
                }
                gameStage.y = startPosY + Math.cos(lt * hertz) * (intensity * et) * y;
                gameStage.x = startPosX + (Math.sin(lt * hertz) * (intensity * et)) * x;
            }
        });
        tl.to({}, {duration: duration});
        return tl;
    }

    public static symbolLandShake(x: number, y: number, duration: number): gsap.core.Timeline {
        const gameStage = NolimitApplication.pixiApp.stage.children[0];
        const tl: gsap.core.Timeline = gsap.timeline({});
        tl.to(gameStage, {duration: duration * 0.7, y: y});
        tl.to(gameStage, {duration: duration * 0.3, y: 0});
        return tl;
    }

}